/* @HEADER
 * @version 1.1.722 | 2026-10-23
 * @purpose Renders the screen the authenticated route (or its hash overlay) means, from the table in authedRouteTable.tsx: the overlays (#account, #organization) sit above the table, the lazy rows inside Suspense (ZVX-DEV-6136, App.tsx-by-meaning S3). ZvenixToolsProvider is not applied here: AppShell's children already render inside the host's one provider.
 * @sidecar AuthedRouteScreen.tsx.header.md
 * @layer app | @hex adapter | @ctx app
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8099, ZVX-DEV-6136, ZVX-DEV-1454, ZVX-DEV-2135, ZVX-DEV-2561

import { Suspense } from 'react'
import type { Route } from './routes'
import { ScreenLoadingFallback } from './ScreenLoadingFallback'
import { resolveHashOverlay } from './hashOverlay'
import { WorkspaceSettingsPage } from '../features/workspace/public-api'
import { AUTHED_ROUTE_SCREENS, type AuthedScreenHost } from './authedRouteTable'
import { AccountPage } from './lazyScreens'
import { LayoutDebugAddress, isLayoutDebugAddress } from '../features/layout-debug/public-api'

export type { AuthedScreenHost, WorkspaceSettingsContext } from './authedRouteTable'

export interface AuthedRouteScreenProps {
  route: Route
  /** The full hash: the overlays are decided from it, above the route. */
  hash: string
  host: AuthedScreenHost
}

/**
 * The screen for this hash: an overlay when the hash names one, the route's screen otherwise.
 *
 * ZVX-DEV-8099 -- `#layout-debug` is neither a route nor an overlay; for the parsers it is an unknown hash and the
 * ordinary screen is what it shows. Only when the hash IS that address does the gate below get asked, and it
 * hands the ordinary screen back unless the operator's lock is open -- so for everybody else the address is an
 * unknown one and the window's chunk is never requested.
 */
export function AuthedRouteScreen(props: AuthedRouteScreenProps) {
  const ordinary = <OrdinaryRouteScreen {...props} />
  if (isLayoutDebugAddress(props.hash)) return <LayoutDebugAddress hash={props.hash} fallback={ordinary} />
  return ordinary
}

function OrdinaryRouteScreen({ route, hash, host }: AuthedRouteScreenProps) {
  const overlay = resolveHashOverlay(hash)
  if (overlay === 'account') {
    return (
      <Suspense fallback={<ScreenLoadingFallback />}>
        <AccountPage />
      </Suspense>
    )
  }
  if (overlay === 'workspace-settings') {
    const ctx = host.readWorkspaceSettingsContext()
    return (
      <WorkspaceSettingsPage
        activeWorkspace={ctx.activeWorkspace}
        currentUser={ctx.currentUser}
        onWorkspaceDeleted={host.onWorkspaceDeleted}
      />
    )
  }
  const spec = AUTHED_ROUTE_SCREENS[route]
  const screen = spec.render(host)
  return spec.lazy ? <Suspense fallback={<ScreenLoadingFallback />}>{screen}</Suspense> : <>{screen}</>
}
